import { useState } from "react";
import { SettingsSection, SettingsSections } from "@/components/settings/SettingsSection";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuthStore } from "@/hooks/useAuthStore";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

export function SecuritySettingsForm() {
  const { toast } = useToast();
  const { t } = useLanguage();
  const authStore = useAuthStore();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);

    try {
      if (!password) {
        throw new Error(t.settings.securitySection.passwordRequired);
      }

      if (password.length < 6) {
        throw new Error(t.settings.securitySection.passwordMinLength);
      }

      if (password !== confirmPassword) {
        throw new Error(t.settings.securitySection.passwordMismatch);
      }

      const success = await authStore.updatePassword(password);
      if (!success) {
        throw new Error(authStore.error || "Failed to update password");
      }

      // Clear password fields on success
      setPassword("");
      setConfirmPassword("");

      toast({
        title: t.settings.securitySection.passwordUpdated,
        description: t.settings.securitySection.passwordUpdatedDesc,
      });
    } catch (error) {
      console.error("Error updating password:", error);
      toast({
        variant: "destructive",
        title: t.settings.securitySection.updateFailed,
        description:
          error instanceof Error ? error.message : t.settings.securitySection.updateFailedDesc,
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <SettingsSections>
      <SettingsSection label={t.settings.securitySection.changePassword}>
        <form onSubmit={handlePasswordChange} className="max-w-xl space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">{t.settings.securitySection.newPassword}</Label>
            <Input
              id="new-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.settings.securitySection.newPasswordPlaceholder}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">{t.settings.securitySection.confirmPassword}</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t.settings.securitySection.confirmPasswordPlaceholder}
            />
          </div>
          <Button type="submit" disabled={passwordLoading} {...pageInfoTarget("settings.password")}>
            {passwordLoading ? t.common.saving : t.settings.securitySection.updatePassword}
          </Button>
        </form>
      </SettingsSection>
    </SettingsSections>
  );
}
